import React, { createContext, useContext } from 'react';
import { BRAND, LOGOS, COLORS } from '@/lib/brand';

/**
 * ThemeContext - Contexto de tema para TUPU Stock
 * 
 * NOTA: La identidad visual de TUPU Stock es FIJA y centralizada en /lib/brand.js
 * Este contexto provee los valores de marca sin permitir personalización.
 * 
 * La empresa cliente puede tener información propia (nombre, locales, operación),
 * pero NO puede modificar la marca TUPU Stock.
 */

// Tema fijo de TUPU Stock (no modificable por el usuario)
const fixedTheme = {
  primaryColor: COLORS.primary.DEFAULT,
  primaryHoverColor: COLORS.primary.hover,
  accentColor: COLORS.accent.DEFAULT,
  backgroundColor: COLORS.background.DEFAULT,
  surfaceColor: COLORS.background.surface,
  borderColor: COLORS.border.DEFAULT,
  textColor: COLORS.text.primary,
  textSecondaryColor: COLORS.text.secondary,
  // Colores de sidebar
  sidebarBg: COLORS.sidebar.bg,
  sidebarBorder: COLORS.sidebar.border,
  sidebarText: COLORS.sidebar.text,
  sidebarActiveBg: COLORS.sidebar.activeBg,
  sidebarActiveText: COLORS.sidebar.activeText,
  // Colores semánticos
  successColor: COLORS.semantic.success,
  warningColor: COLORS.semantic.warning,
  errorColor: COLORS.semantic.error,
  // Bordes redondeados
  borderRadius: '0.75rem',
};

// Logos fijos de TUPU Stock (no modificables por el usuario)
const fixedLogos = {
  logoHorizontal: LOGOS.horizontal,
  logoSquare: LOGOS.square,
  logoLogin: LOGOS.login,
  siteName: BRAND.name,
  tagline: BRAND.tagline,
};

const ThemeContext = createContext(null);

export const ThemeProvider = ({ children }) => {
  // Los valores son fijos - no hay funciones de actualización reales
  const theme = fixedTheme;
  const logos = fixedLogos;

  // Funciones vacías por compatibilidad con código legacy
  const updateTheme = () => {
    console.warn('ThemeProvider: La personalización de tema está deshabilitada en TUPU Stock');
  };
  
  const resetTheme = () => {
    console.warn('ThemeProvider: La personalización de tema está deshabilitada en TUPU Stock');
  };
  
  const updateLogos = () => {
    console.warn('ThemeProvider: La personalización de logos está deshabilitada en TUPU Stock');
  };
  
  const resetLogos = () => {
    console.warn('ThemeProvider: La personalización de logos está deshabilitada en TUPU Stock');
  };

  const value = {
    theme,
    logos,
    // Funciones deshabilitadas (mantienen compatibilidad pero no hacen nada)
    updateTheme,
    resetTheme,
    defaultTheme: fixedTheme,
    updateLogos,
    resetLogos,
    // Nuevo: indicador de que el branding es fijo
    isBrandingLocked: true,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
